import { useState, useEffect, useCallback } from 'react';
import { API } from 'aws-amplify';
import { getAllocationRulesForConfig } from '../../../graphql/queries';
import { allocationRulesEditProps } from '../../../types/componentTypes/allocationRules';
import { getCurrencies } from './../../../pages/Currencies/services/api-calls';
import { useParams } from 'react-router-dom';
import { CurrencyTableItem } from '../../../types/componentTypes/CurrencyTypes';
import { allocationRules } from '../../../types/componentTypes/allocationRules';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Paper,
  Skeleton,
  Link,
} from '@mui/material';
import '../CommonStyles.css';
import { DeleteAllocationRule } from '../../../components/modals/DeleteAllocationRuleModal';
import { collectAndGetRef, currenciesRef } from '../../../constants/helpful-link';
import { saveConfig } from '../../../utils/s3FileUtils';
const uniqid = require('uniqid');

const defaultHeaders = ['Currency Name and ID', 'Amount', 'Program ID', 'Lot ID/Campaign ID', 'Validity', 'Actions'];
const receiptScannerHeaders = ['Currency Name and ID', 'SKU', 'Amount', 'Actions'];

const DeleteAllocationRuleButton = ({
  moreThanOneRule,
  setNotificationState,
  onClickActions,
}: {
  moreThanOneRule: boolean;
  setNotificationState: any;
  onClickActions: Array<() => void>;
}) => {
  const onClickHandler = () => {
    onClickActions.forEach((action) => action());
  };
  return (
    <ClearIcon
      color={moreThanOneRule ? 'action' : 'disabled'}
      style={{ cursor: moreThanOneRule ? 'pointer' : 'not-allowed' }}
      onClick={() => {
        if (!moreThanOneRule) {
          setNotificationState({
            open: true,
            title: 'Cannot delete',
            content: 'You must have at least a single allocation rule for a Collect and Get promotion.',
            level: 'warning',
          });
          return;
        }
        onClickHandler();
      }}
    />
  );
};

const AllocationRules = ({ config, setShowAllocationRulePage, setConfig, setNotificationState }: allocationRulesEditProps) => {
  const [rules, setRules] = useState<Array<allocationRules>>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [availableCurrencies, setAvailableCurrencies] = useState<CurrencyTableItem[]>();
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedRule, setSelectedRule] = useState<allocationRules>();
  let urlParams = useParams();

  const fetchAndFilterCurrencies = useCallback((countryToDisplay: string) => {
    try {
      getCurrencies().then((fetchedCurrencies) => {
        setAvailableCurrencies((fetchedCurrencies as CurrencyTableItem[]).filter((currency) => currency.country === countryToDisplay));
      });
    } catch (e) {
      console.log('Failed to fetch market relevant currencies with', e);
    }
  }, []);

  const retrieveAllocationRules = useCallback(async () => {
    const queryResult: any = await API.graphql({
      query: getAllocationRulesForConfig,
      variables: { configuration_id: config.configurationId },
    });
    const rulesData = queryResult.data.getAllocationRulesForConfig.items;
    return rulesData;
  }, [config.configurationId]);

  const proofType = config.pluginParams ? 'receiptScanner' : 'mixCodes';

  let receiptScannerRules: Array<{ SKU: string; currencyId: string; amount: number }> = [];
  if (proofType === 'receiptScanner' && config.pluginParams?.rsAllocationMap) {
    receiptScannerRules = Object.entries(config.pluginParams.rsAllocationMap).flatMap(([sku, arr]) =>
      (arr as Array<{ currencyId: string; amount: number }>).map((rule) => ({
        SKU: sku,
        currencyId: rule.currencyId,
        amount: rule.amount,
      }))
    );
  }

  useEffect(() => {
    const loadData = async () => {
      fetchAndFilterCurrencies(config?.configurationParameters?.country);
      if (proofType === 'mixCodes') {
        retrieveAllocationRules()
          .then((data) => {
            const rulesData = data.filter((rule: allocationRules) => Boolean(rule.rule_active) === true);
            setRules(rulesData);
            setLoading(false);
          })
          .catch((e) => {
            console.error('Error while retrieving rules info', e);
          });
      }
    };
    loadData().then(() => {
      setLoading(false);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, fetchAndFilterCurrencies, retrieveAllocationRules]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (proofType === 'receiptScanner' ? receiptScannerRules.length : rules.length)) : 0;

  const closeModal = () => {
    setSelectedRule(undefined);
    setConfirmDelete(false);
  };

  const handleDeleteRSAllocationRule = async (rule: { SKU: string; currencyId: string }) => {
    const updatedConfig = { ...config };
    delete updatedConfig.pluginParams.rsAllocationMap[rule.SKU];
    setConfig(updatedConfig);
    await saveConfig({ urlParams, submitData: updatedConfig, setNotificationState });
    setNotificationState({
      open: true,
      title: 'Allocation rule deleted successfully',
      content: 'Allocation rule was deleted!',
      level: 'success',
    });
  };

  const refreshRules = () => {
    setLoading(true);
    retrieveAllocationRules()
      .then((data) => {
        const rulesData = data.filter((rule: allocationRules) => Boolean(rule.rule_active) === true);
        setRules(rulesData);
        setLoading(false);
      })
      .catch((e) => {
        console.error('Error while retrieving rules info', e);
      });
  };

  return (
    <>
      <Box>
        <DeleteAllocationRule
          modalOpenState={confirmDelete}
          rule={selectedRule}
          handleClose={() => {
            closeModal();
            refreshRules();
          }}
        />
        <Box className="tabHeader">
          <Typography variant="h3">Allocation Rules</Typography>
          <Button
            variant="contained"
            color="primary"
            className="firstHeaderButton"
            onClick={() => {
              setShowAllocationRulePage({ showScreen: true });
            }}
          >
            + Add Allocation Rule
          </Button>
        </Box>
        <TableContainer component={Paper} className="vouchersContainer">
          <Table className="vouchersTable">
            <TableHead>
              <TableRow className="voucherHeader">
                {(proofType === 'receiptScanner' ? receiptScannerHeaders : defaultHeaders).map((header, i) => (
                  <TableCell key={i} align="center">
                    <Typography variant="body2">{header}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                (proofType === 'receiptScanner'
                  ? receiptScannerRules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : rules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                ).map((rule: any, i: number) => (
                  <TableRow key={uniqid()}>
                    <TableCell align="center">
                      {availableCurrencies?.find(
                        (currObject) => currObject.currency_id === (proofType === 'receiptScanner' ? rule.currencyId : rule.currency_id)
                      )?.name
                        ? `${
                            availableCurrencies.find(
                              (currObject) =>
                                currObject.currency_id === (proofType === 'receiptScanner' ? rule.currencyId : rule.currency_id)
                            )?.name
                          } (${proofType === 'receiptScanner' ? rule.currencyId : rule.currency_id})`
                        : proofType === 'receiptScanner'
                        ? rule.currencyId
                        : rule.currency_id}
                    </TableCell>
                    {proofType === 'receiptScanner' ? (
                      <>
                        <TableCell align="center">{rule.SKU}</TableCell>
                        <TableCell align="center">{rule.amount}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => {
                              setShowAllocationRulePage({ showScreen: true, state: rule, additionalProps: { availableCurrencies } });
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <DeleteAllocationRuleButton
                            moreThanOneRule={receiptScannerRules.length > 1}
                            setNotificationState={setNotificationState}
                            onClickActions={[
                              () => handleDeleteRSAllocationRule(rule),
                            ]}
                          />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="center">{rule.amount}</TableCell>
                        <TableCell align="center">{rule.program_id}</TableCell>
                        <TableCell align="center">{rule.lot_id}</TableCell>
                        <TableCell align="center">{rule.validity}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => {
                              setShowAllocationRulePage({ showScreen: true, state: rule });
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                           <DeleteAllocationRuleButton
                            moreThanOneRule={rules.length > 1}
                            setNotificationState={setNotificationState}
                            onClickActions={[
                              () =>  setConfirmDelete(true),
                              () => setSelectedRule(rule)
                            ]}
                          />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              {loading &&
                Array(7)
                  .fill(1)
                  .map((el, i) => (
                    <TableRow key={i}>
                      {Array(proofType === 'receiptScanner' ? 4 : 6)
                        .fill(1)
                        .map((el, j) => (
                          <TableCell key={j}>
                            <Skeleton />
                            <Skeleton width="60%" />
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
              {emptyRows > 0 && (
                <TableRow key={uniqid()} style={{ height: 50 * emptyRows }}>
                  <TableCell colSpan={proofType === 'receiptScanner' ? 4 : 6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 15, 20, 25, { label: 'All', value: -1 }]}
          component="div"
          count={proofType === 'receiptScanner' ? receiptScannerRules.length : rules.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className="tablePagination"
        />
        <Typography variant="h3" className="links-title">
          Helpful Links
        </Typography>
        <Box className="helpfulLinks">
          <Link target="_blank" href={currenciesRef.ALLOCATION_RULES} rel="noopener noreferrer">
            Allocation Rules
          </Link>
          <Link target="_blank" href={collectAndGetRef.ADD_CG} rel="noopener noreferrer">
            How to add a Collect and Get promotion (Wiki and Video)
          </Link>
        </Box>
      </Box>
    </>
  );
};

export { AllocationRules };
